"use client";

import React, { useCallback, useEffect, useState } from "react";
import { PageHeader } from "@/components/layout/PageHeader";
import { StatCard } from "@/components/ui/StatCard";
import { ProductRequest, StockSummaryRow } from "@/types";
import { CreateRequestModal } from "@/components/modals/CreateRequestModal";
import {
  fetchStockRequests,
  StockRequestApiItem,
  StockRequestStatusUpdate,
  StockSummaryCardApi,
  updateStockRequestStatus,
} from "@/services/stockSummaryService";
import { handleApiError } from "@/utils/api";
import { formatPriceUnit } from "@/utils/display";
import {
  DEFAULT_LIMIT,
  DEFAULT_PAGE,
  formatDetailValue,
  formattedCurrency,
  getCardConfig,
  getDetailedEntries,
  getDetailedStockEntries,
  getDisplayStatus,
  toTitleCase,
} from "@/constants";
import StockSummaryConfirmationModal from "../ui/StockSummaryConfirmationModal";
import StockSummaryFilterOpions from "../ui/StockSummaryFilterOpions";
import RequestDetails from "../ui/RequestDetails";


const mapRequestRow = (request: StockRequestApiItem): StockSummaryRow => ({
  id: request.id,
  sourceType: request.sourceType || "request",
  rawStatus: request.status,
  createdAt: request.createdAt || "",
  details: request,
  userName: request.userName || "--",
  operation: toTitleCase(
    request.operationLabel || request.operationType,
  ) as ProductRequest["operation"],
  product: request.productName || "--",
  quantity: request.quantity || "--",
  amount: formattedCurrency(request.amount),
  city: request.city?.trim() || "--",
  status: getDisplayStatus(request.status, request.statusLabel),
});

export const StockSummary: React.FC = () => {
  const [isCreateRequestOpen, setIsCreateRequestOpen] = useState(false);
  const [allRequests, setAllRequests] = useState<StockSummaryRow[]>([]);
  const [summaryCards, setSummaryCards] = useState<StockSummaryCardApi[]>([]);
  const [tableLoading, setTableLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmationAction, setConfirmationAction] = useState<{
    requestId: string;
    status: StockRequestStatusUpdate;
    sourceType: string;
  } | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [cancellationReason, setCancellationReason] = useState("");
  const [selectedRequest, setSelectedRequest] =
    useState<StockSummaryRow | null>(null);
  const [paginationMeta, setPaginationMeta] = useState({
    total: 0,
    page: DEFAULT_PAGE,
    limit: DEFAULT_LIMIT,
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [operationFilter, setOperationFilter] = useState("All");
  const [cityFilter, setCityFilter] = useState("All");
  const [statusFilter, setStatusFilter] = useState("All");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const loadStockRequests = useCallback(async () => {
    setTableLoading(true);

    try {
      const firstPage = await fetchStockRequests(
        1,
        Math.max(paginationMeta.limit, DEFAULT_LIMIT),
        debouncedSearch,
        startDate || undefined,
        endDate || undefined,
      );

      let combinedRequests = [...firstPage.requests];
      const totalPagesFromApi = Math.max(
        1,
        Math.ceil(firstPage.total / firstPage.limit),
      );

      for (let page = 2; page <= totalPagesFromApi; page += 1) {
        const nextPage = await fetchStockRequests(
          page,
          firstPage.limit,
          debouncedSearch,
          startDate || undefined,
          endDate || undefined,
        );
        combinedRequests = combinedRequests.concat(nextPage.requests);
      }

      setAllRequests(combinedRequests.map(mapRequestRow));
      setSummaryCards(firstPage.summary.cards);
      setError(null);
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setTableLoading(false);
    }
  }, [debouncedSearch, endDate, paginationMeta.limit, startDate]);

  useEffect(() => {
    loadStockRequests();
  }, [loadStockRequests]);

  useEffect(() => {
    const handle = setTimeout(() => {
      setPaginationMeta((prev) => ({ ...prev, page: 1 }));
      setDebouncedSearch(searchTerm.trim());
    }, 1000);

    return () => clearTimeout(handle);
  }, [searchTerm]);

  const handleAddRequest = async () => {
    await loadStockRequests();
  };

  const openConfirmationModal = (
    requestId: string,
    status: StockRequestStatusUpdate,
    sourceType: string,
  ) => {
    setCancellationReason("");
    setConfirmationAction({ requestId, status, sourceType });
  };

  const closeConfirmationModal = () => {
    if (actionLoading) {
      return;
    }

    setCancellationReason("");
    setConfirmationAction(null);
  };

  const handleConfirmAction = async () => {
    if (!confirmationAction) {
      return;
    }

    setActionLoading(true);

    try {
      await updateStockRequestStatus(
        confirmationAction.requestId,
        confirmationAction.status,
        confirmationAction.sourceType,
        confirmationAction.status === "CANCELLED"
          ? cancellationReason
          : undefined,
      );

      setError(null);
      setCancellationReason("");
      setConfirmationAction(null);
      await loadStockRequests();
      setSelectedRequest(null);
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setActionLoading(false);
    }
  };

  const isProcessedRequest = (request: StockSummaryRow) =>
    request.status === "Completed" || request.status === "Cancelled";
  
  const isProcessingRequest = (request: StockSummaryRow) =>
    request.status === "Processing";

  const isCurrentAction = (requestId: string) =>
    actionLoading && confirmationAction?.requestId === requestId;

  const closeDetailsPanel = () => setSelectedRequest(null);

  const operationOptions = [
    "All",
    ...Array.from(new Set(allRequests.map((req) => req.operation))),
  ];
  const cityOptions = [
    "All",
    ...Array.from(
      new Set(allRequests.map((req) => req.city).filter(Boolean)),
    ),
  ];
  const statusOptions = [
    "All",
    ...Array.from(new Set(allRequests.map((req) => req.status))),
  ];

  const filteredRequests = allRequests.filter((req) => {
    const matchesOperation =
      operationFilter === "All" || req.operation === operationFilter;
    const matchesCity = cityFilter === "All" || req.city === cityFilter;
    const matchesStatus = statusFilter === "All" || req.status === statusFilter;
    return matchesOperation && matchesCity && matchesStatus;
  });

  const totalPages = Math.max(1, Math.ceil(filteredRequests.length / paginationMeta.limit));
  const paginatedFilteredRequests = filteredRequests.slice(
    (paginationMeta.page - 1) * paginationMeta.limit,
    paginationMeta.page * paginationMeta.limit,
  );

  useEffect(() => {
    setPaginationMeta((prev) => {
      const nextPage = Math.min(prev.page, totalPages);
      if (prev.total === filteredRequests.length && prev.page === nextPage) {
        return prev;
      }

      return {
        ...prev,
        total: filteredRequests.length,
        page: nextPage,
      };
    });
  }, [filteredRequests.length, totalPages]);

  useEffect(() => {
    setPaginationMeta((prev) =>
      prev.page === 1 ? prev : { ...prev, page: 1 },
    );
  }, [operationFilter, cityFilter, statusFilter, startDate, endDate]);

  return (
    <div>
      {error && <div className="pt-2 mt-5 text-center text-red-500">{error}</div>}

      <PageHeader
        title="Product Request Summary"
        subtitle="Raw material inventory requests and stock movements"
        showSearch
        searchPlaceholder="Search record by product name"
        searchValue={searchTerm}
        onSearchChange={setSearchTerm}
      />

      <div className="mb-8 grid grid-cols-2 gap-3 md:gap-4 xl:grid-cols-5">
        {summaryCards.map((card) => {
          const config = getCardConfig(card);

          return (
            <StatCard
              key={card.key}
              label={card.label.toUpperCase()}
              value={card.value}
              badge={config.badge}
              badgeColor={config.badgeColor}
              icon={config.icon}
            />
          );
        })}
      </div>
      <StockSummaryFilterOpions
        operationFilter={operationFilter}
        setOperationFilter={setOperationFilter}
        cityFilter={cityFilter}
        setCityFilter={setCityFilter}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
        filteredRequests={paginatedFilteredRequests}
        tableLoading={tableLoading}
        totalPages={totalPages}
        setPaginationMeta={setPaginationMeta}
        operationOptions={operationOptions}
        cityOptions={cityOptions}
        statusOptions={statusOptions}
        setSelectedRequest={setSelectedRequest}
        isProcessedRequest={isProcessedRequest}
        isProcessingRequest={isProcessingRequest}
        openConfirmationModal={openConfirmationModal}
        isCurrentAction={isCurrentAction}
        formatPriceUnit={formatPriceUnit}
        paginationMeta={paginationMeta}
      />

      <CreateRequestModal
        isOpen={isCreateRequestOpen}
        onClose={() => setIsCreateRequestOpen(false)}
        onAddRequest={handleAddRequest}
      />

      <StockSummaryConfirmationModal
        confirmationAction={confirmationAction}
        closeConfirmationModal={closeConfirmationModal}
        actionLoading={actionLoading}
        cancellationReason={cancellationReason}
        setCancellationReason={setCancellationReason}
        handleConfirmAction={handleConfirmAction}
      />
      <div
        className={`fixed inset-0 z-40 bg-slate-900/30 transition-opacity ${
          selectedRequest
            ? "pointer-events-auto opacity-100"
            : "pointer-events-none opacity-0"
        }`}
        onClick={closeDetailsPanel}
        aria-hidden={!selectedRequest}
      />
      <RequestDetails
        selectedRequest={selectedRequest}
        closeDetailsPanel={closeDetailsPanel}
        formatDetailValue={formatDetailValue}
        getDetailedEntries={getDetailedStockEntries}
      />
    </div>
  );
};
